import { useQuery } from "react-query";
import useAPI from "./useAPI";

/** Used only until the real admin-configured default has loaded. */
export const DEFAULT_SORT_FALLBACK = [
    { field: "has_image", direction: "desc" },
    { field: "new", direction: "desc" },
];

/** Where the user's own last manually-picked sort is remembered, across page loads. */
export const USER_SORT_STORAGE_KEY = "userSelectedProductSort";

/** Parse a single `field___direction` string into `{field, direction}`, or null if invalid/empty. */
const parseSortEntry = (raw) => {
    if (!raw) return null;
    const [field, direction] = String(raw).split("___");
    return field && direction ? { field, direction } : null;
};

const parseSecondarySort = (raw) => {
    try {
        // Axios auto-parses response bodies that are valid JSON (which `["a___b"]` is), so `raw`
        // may already be an array here rather than the JSON string it looks like server-side.
        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (Array.isArray(parsed) && parsed.length) {
            return parsed.map(parseSortEntry).filter(Boolean);
        }
    } catch (error) {
        // Fall through to the default below
    }
    return [];
};

/**
 * The admin-configured default product-list sort (primary + secondary/tie-break rules),
 * fetched fresh from the backend on every session — deliberately not cached in localStorage,
 * since a client-persisted copy of server config goes stale the moment an admin changes it.
 * React Query's own in-memory cache still dedupes calls across the pages/components that use this.
 */
const useDefaultProductSort = () => {
    const api = useAPI();

    // Cached for 5 minutes so normal navigation doesn't refetch this on every mount — an admin's
    // CMS change is visible within 5 minutes (or immediately after a hard refresh, which clears
    // this cache entirely). `isFetching` (not `isLoading`) is used below so callers also wait out
    // a background refetch once the cache goes stale, not just the very first load.
    const { data, isFetching } = useQuery(
        ["defaultProductSort"],
        () =>
            Promise.all([
                api.get("configuration/b2b_basic_configuration/display_products_list/default_sort_order/value", false),
                api.get("configuration/b2b_basic_configuration/display_products_list/default_secondary_sort_order/value", false),
            ]),
        { refetchOnWindowFocus: false, staleTime: 5 * 60 * 1000 }
    );

    const [primaryRaw, secondaryRaw] = data ?? [];

    const cmsDefaultPrimary = parseSortEntry(primaryRaw) ?? { field: "new", direction: "desc" };
    const secondary = parseSecondarySort(secondaryRaw);

    /**
     * Resolve the sort array to use, secondary (tie-break) rules first, then the primary sort —
     * chosen by priority: an explicit, valid `urlSortValue` > the user's own last manually-picked
     * sort (remembered in localStorage) > the CMS-configured default.
     *
     * @param {?string} urlSortValue The raw `field___direction` value from the URL's `sort` param, if any.
     * @returns {Array<{field: string, direction: string}>}
     */
    const resolveSort = (urlSortValue) => {
        const primary = parseSortEntry(urlSortValue) ?? parseSortEntry(localStorage.getItem(USER_SORT_STORAGE_KEY)) ?? cmsDefaultPrimary;
        return [...secondary, primary];
    };

    return { secondary, resolveSort, sort: resolveSort(), isLoading: isFetching };
};

export default useDefaultProductSort;
